import React, { useState, useEffect, useCallback } from 'react';
import { scheduleApi, sessionConfigApi, therapistApi, studentApi, guestStudentApi } from '../../services/api';
import { Schedule, Therapist, Student, SessionConfig, SessionTimeSlotsByDay, GuestStudent } from '../../types';
import { handleApiError, handleSuccess } from '../../utils/errorHandler';
import SimpleDropdown from './SimpleDropdown';
import './ScheduleCreationForm.css';

interface ScheduleCreationFormProps {
  onSubmit: (schedule: Schedule) => void;
  onCancel: () => void;
  existingSchedule?: Schedule;
  isEditMode?: boolean;
}

interface StudentAssignment {
  participantType: 'registered' | 'guest' | 'break';
  studentId?: number;
  guestStudentId?: number;
  guestStudent?: GuestStudent;
  sessionTitle: string;
  dayOfWeek: string;
  startTime: string;
  endTime: string;
  assignmentDate: string;
  notes?: string;
  assignmentType: 'therapy' | 'break' | 'group_activity';
}

const ScheduleCreationForm: React.FC<ScheduleCreationFormProps> = ({
  onSubmit,
  onCancel,
  existingSchedule,
  isEditMode = false
}) => {
  const [formData, setFormData] = useState({
    therapistId: '',
    weekStartDate: '',
    weekEndDate: '',
    sessionConfigId: ''
  });
  
  const [therapists, setTherapists] = useState<Therapist[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [sessionConfig, setSessionConfig] = useState<SessionConfig | null>(null);
  const [timeSlots, setTimeSlots] = useState<SessionTimeSlotsByDay | null>(null);
  const [assignments, setAssignments] = useState<StudentAssignment[]>([]);
  const [guestStudents, setGuestStudents] = useState<GuestStudent[]>([]);
  const [guestFormState, setGuestFormState] = useState<{
    day: string;
    sessionTitle: string;
    startTime: string;
    endTime: string;
  } | null>(null);
  const [guestFormData, setGuestFormData] = useState({
    fullName: '',
    contactInfo: '',
    notes: ''
  });
  const [guestFormMode, setGuestFormMode] = useState<'existing' | 'new'>('new');
  const [selectedExistingGuestId, setSelectedExistingGuestId] = useState<string>('');
  const [guestFormLoading, setGuestFormLoading] = useState(false);
  const [guestFormError, setGuestFormError] = useState<string | null>(null);

  useEffect(() => {
    if (guestFormMode === 'existing' && guestStudents.length > 0 && !selectedExistingGuestId) {
      setSelectedExistingGuestId(String(guestStudents[0].id ?? ''));
    }
  }, [guestFormMode, guestStudents, selectedExistingGuestId]);

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Get available days dynamically from session configuration
  const days = timeSlots ? Object.keys(timeSlots).filter(day => 
    timeSlots[day as keyof SessionTimeSlotsByDay].length > 0
  ) : ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  const generateWeekDates = useCallback(() => {
    const startDate = new Date(formData.weekStartDate);
    const endDate = new Date(formData.weekEndDate);
    
    // Validate date range
    if (startDate >= endDate) {
      setErrors(prev => ({ ...prev, weekEndDate: 'End date must be after start date' }));
      return;
    }
    
    // Clear date errors
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.weekStartDate;
      delete newErrors.weekEndDate;
      return newErrors;
    });
  }, [formData.weekStartDate, formData.weekEndDate]);

  useEffect(() => {
    loadInitialData();
  }, []);

  // Helper function to format date for HTML date input (YYYY-MM-DD)
  const formatDateForInput = (dateString: string): string => {
    if (!dateString) return '';

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';

      // Convert to YYYY-MM-DD format
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');

      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };

  const handleExistingGuestAssignment = (
    day: string,
    sessionTitle: string,
    startTime: string,
    endTime: string,
    guest: GuestStudent
  ) => {
    const assignmentDate = getDateForDay(day) || '';
    const newAssignment: StudentAssignment = {
      participantType: 'guest',
      guestStudentId: guest.id,
      guestStudent: guest,
      sessionTitle,
      dayOfWeek: day,
      startTime,
      endTime,
      assignmentDate,
      notes: '',
      assignmentType: 'therapy'
    };

    setAssignmentForSlot(newAssignment);
  };

  const refreshGuestStudents = async () => {
    try {
      const response = await guestStudentApi.getAll({ limit: 200 });
      setGuestStudents(response.data.guestStudents);
      return response.data.guestStudents;
    } catch (error) {
      console.error('Failed to refresh guest students:', error);
      return guestStudents;
    }
  };

  const openGuestForm = async (params: { day: string; sessionTitle: string; startTime: string; endTime: string }) => {
    const latestGuests = await refreshGuestStudents();
    setGuestFormState(params);
    setGuestFormData({ fullName: '', contactInfo: '', notes: '' });
    setGuestFormError(null);
    if (latestGuests.length > 0) {
      setGuestFormMode('existing');
      setSelectedExistingGuestId(String(latestGuests[0].id ?? ''));
    } else {
      setGuestFormMode('new');
      setSelectedExistingGuestId('');
    }
  };

  const closeGuestForm = () => {
    setGuestFormState(null);
    setGuestFormData({ fullName: '', contactInfo: '', notes: '' });
    setGuestFormError(null);
    setGuestFormLoading(false);
    setSelectedExistingGuestId('');
    setGuestFormMode('new');
  };

  const handleGuestFormInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setGuestFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleGuestFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!guestFormState) return;

    if (guestFormMode === 'existing') {
      if (!selectedExistingGuestId) {
        setGuestFormError('Select a guest to continue');
        return;
      }

      const guest = guestStudents.find(g => String(g.id) === selectedExistingGuestId);
      if (!guest) {
        setGuestFormError('Selected guest not found');
        return;
      }

      handleExistingGuestAssignment(
        guestFormState.day,
        guestFormState.sessionTitle,
        guestFormState.startTime,
        guestFormState.endTime,
        guest
      );
      closeGuestForm();
      return;
    }

    const trimmedName = guestFormData.fullName.trim();
    if (!trimmedName) {
      setGuestFormError('Guest name is required');
      return;
    }

    setGuestFormLoading(true);
    setGuestFormError(null);

    try {
      const response = await guestStudentApi.create({
        fullName: trimmedName,
        contactInfo: guestFormData.contactInfo.trim() || undefined,
        notes: guestFormData.notes.trim() || undefined
      });

      const newGuest = response.data.guestStudent;
      setGuestStudents(prev => [newGuest, ...prev]);

      handleExistingGuestAssignment(
        guestFormState.day,
        guestFormState.sessionTitle,
        guestFormState.startTime,
        guestFormState.endTime,
        newGuest
      );
      closeGuestForm();
    } catch (error: any) {
      console.error('Failed to create guest student:', error);
      setGuestFormError(error.error || 'Failed to create guest student');
      setGuestFormLoading(false);
    }
  };

  // Populate form with existing schedule data when in edit mode
  useEffect(() => {
    if (isEditMode && existingSchedule) {
      console.log('Populating form with existing schedule:', existingSchedule);

      // Ensure we have valid data before setting form
      const therapistId = existingSchedule.therapistId || existingSchedule.createdBy || '';
      const sessionConfigId = existingSchedule.sessionConfigId || '';

      setFormData({
        therapistId: therapistId ? therapistId.toString() : '',
        weekStartDate: formatDateForInput(existingSchedule.weekStartDate || ''),
        weekEndDate: formatDateForInput(existingSchedule.weekEndDate || ''),
        sessionConfigId: sessionConfigId ? sessionConfigId.toString() : ''
      });

      // Load existing assignments if available
      console.log('🔍 Checking for existing assignments:', existingSchedule.assignments);
      console.log('🔍 existingSchedule keys:', Object.keys(existingSchedule));
      console.log('🔍 Full existingSchedule object:', JSON.stringify(existingSchedule, null, 2));

      if (existingSchedule.assignments && Array.isArray(existingSchedule.assignments) && existingSchedule.assignments.length > 0) {
        console.log('✅ Loading existing assignments:', existingSchedule.assignments);
        const existingAssignments = existingSchedule.assignments.map(assignment => {
          console.log('🔄 Processing assignment:', assignment);
          // Handle both camelCase and snake_case from backend using type assertion
          const a = assignment as any;
          const rawGuest = a.guestStudent || a.guest_student;
          const participantType: StudentAssignment['participantType'] =
            (a.assignmentType || a.assignment_type) === 'break'
              ? 'break'
              : rawGuest
                ? 'guest'
                : (a.studentId || a.student_id)
                  ? 'registered'
                  : 'registered';

          const guestStudent: GuestStudent | undefined = rawGuest
            ? {
                id: rawGuest.id || rawGuest.guest_student_id || rawGuest.guestStudentId,
                fullName: rawGuest.fullName || rawGuest.full_name || '',
                contactInfo: rawGuest.contactInfo || rawGuest.contact_info || undefined,
                notes: rawGuest.notes || undefined
              }
            : undefined;

          return {
            participantType,
            studentId: participantType === 'registered' ? (a.studentId || a.student_id || 0) : undefined,
            guestStudentId: participantType === 'guest'
              ? (guestStudent?.id || a.guestStudentId || a.guest_student_id || undefined)
              : undefined,
            guestStudent: participantType === 'guest' ? guestStudent : undefined,
            sessionTitle: a.activityName || a.activity_name || '',
            dayOfWeek: a.dayOfWeek || a.day_of_week || '',
            startTime: a.startTime || a.start_time || '',
            endTime: a.endTime || a.end_time || '',
            assignmentDate: a.assignmentDate || a.assignment_date || '',
            notes: a.notes || '',
            assignmentType: (a.assignmentType || a.assignment_type || 'therapy') as 'therapy' | 'break' | 'group_activity'
          };
        });
        console.log('✅ Converted assignments for form:', existingAssignments);
        setAssignments(existingAssignments);
      } else {
        console.log('❌ No existing assignments found, clearing assignments array');
        console.log('   - assignments exists:', !!existingSchedule.assignments);
        console.log('   - is array:', Array.isArray(existingSchedule.assignments));
        console.log('   - length:', existingSchedule.assignments?.length);
        setAssignments([]);
      }
    }
  }, [isEditMode, existingSchedule]);

  useEffect(() => {
    if (formData.weekStartDate && formData.weekEndDate) {
      generateWeekDates();
    }
  }, [formData.weekStartDate, formData.weekEndDate, generateWeekDates]);

  const loadInitialData = async () => {
    console.log('Loading initial data...');
    try {
      // Load therapists
      const therapistsResult = await therapistApi.getAll({ active: true, limit: 100 });
      console.log('Therapists loaded:', therapistsResult.data.therapists);
      setTherapists(therapistsResult.data.therapists);

      // Load students
      const studentsResult = await studentApi.getAll({ active: true, limit: 100 });
      console.log('Students loaded:', studentsResult.data.students);
      setStudents(studentsResult.data.students);

      // Load guest students
      const guestResult = await guestStudentApi.getAll({ limit: 200 });
      setGuestStudents(guestResult.data.guestStudents);

      // Load default session configuration
      try {
        const configResult = await sessionConfigApi.getDefault();
        console.log('Session config loaded:', configResult.data.config);
        console.log('Time slots loaded:', configResult.data.timeSlots);
        setSessionConfig(configResult.data.config);
        setTimeSlots(configResult.data.timeSlots);
        setFormData(prev => ({ ...prev, sessionConfigId: configResult.data.config.id.toString() }));
      } catch (configError) {
        console.error('Failed to load session configuration:', configError);
        // Show user-friendly message about missing session configuration
        setErrors(prev => ({ 
          ...prev, 
          sessionConfig: 'No session configuration found. Please create a session configuration in Settings first.' 
        }));
      }
    } catch (error) {
      console.error('Failed to load initial data:', error);
      handleApiError(error as any, 'Failed to load initial data');
    }
  };



  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const setAssignmentForSlot = (assignment: StudentAssignment) => {
    const normalizeTime = (time: string) => time.split(':').slice(0, 2).join(':');
    const filteredAssignments = assignments.filter(
      a =>
        !(
          a.dayOfWeek === assignment.dayOfWeek &&
          normalizeTime(a.startTime) === normalizeTime(assignment.startTime) &&
          normalizeTime(a.endTime) === normalizeTime(assignment.endTime)
        )
    );

    setAssignments([...filteredAssignments, assignment]);
  };

  const handleRegisteredStudentAssignment = (
    day: string,
    sessionTitle: string,
    startTime: string,
    endTime: string,
    studentId: string
  ) => {
    console.log('handleStudentAssignment called with:', { day, sessionTitle, startTime, endTime, studentId });
    
    if (!studentId) {
      console.log('No studentId provided, returning');
      return;
    }

    // Allow assignment even without dates - we'll validate on submit
    const assignmentDate = getDateForDay(day) || ''; // Use empty string if no date

    const newAssignment: StudentAssignment = {
      studentId: parseInt(studentId),
      sessionTitle,
      dayOfWeek: day,
      startTime,
      endTime,
      assignmentDate,
      notes: '',
      assignmentType: 'therapy',
      participantType: 'registered'
    };

    console.log('Creating new assignment:', newAssignment);

    setAssignmentForSlot(newAssignment);
    console.log('Assignment added successfully');
  };

  const removeStudentAssignment = (day: string, startTime: string, endTime: string) => {
    // Normalize time format for consistent comparison
    const normalizeTime = (time: string) => time.split(':').slice(0, 2).join(':');
    const normalizedStartTime = normalizeTime(startTime);
    const normalizedEndTime = normalizeTime(endTime);
    
    setAssignments(assignments.filter(
      a => !(a.dayOfWeek === day && 
             normalizeTime(a.startTime) === normalizedStartTime && 
             normalizeTime(a.endTime) === normalizedEndTime)
    ));
  };

  const getDateForDay = (dayName: string): string => {
    if (!formData.weekStartDate) return '';
    
    const startDate = new Date(formData.weekStartDate);
    const dayIndex = days.indexOf(dayName);
    const targetDate = new Date(startDate);
    targetDate.setDate(startDate.getDate() + dayIndex);
    
    return targetDate.toISOString().split('T')[0];
  };

  const getAssignmentForSlot = (day: string, startTime: string, endTime: string): StudentAssignment | undefined => {
    // Normalize time format - remove seconds if present for comparison
    const normalizeTime = (time: string) => time.split(':').slice(0, 2).join(':');
    const normalizedStartTime = normalizeTime(startTime);
    const normalizedEndTime = normalizeTime(endTime);

    console.log(`🔍 Looking for assignment: day=${day}, startTime=${normalizedStartTime}, endTime=${normalizedEndTime}`);
    console.log('🔍 Available assignments:', assignments.map(a => ({
      day: a.dayOfWeek,
      start: normalizeTime(a.startTime),
      end: normalizeTime(a.endTime),
      student: a.studentId
    })));

    const assignment = assignments.find(a =>
      a.dayOfWeek === day &&
      normalizeTime(a.startTime) === normalizedStartTime &&
      normalizeTime(a.endTime) === normalizedEndTime
    );

    console.log('🔍 Found assignment:', assignment);
    return assignment;
  };

  const validateForm = (): boolean => {
    console.log('Validating form...');
    console.log('Session config:', sessionConfig);
    console.log('Time slots:', timeSlots);
    
    const newErrors: Record<string, string> = {};

    if (!formData.therapistId) {
      newErrors.therapistId = 'Therapist selection is required';
    }

    if (!formData.weekStartDate) {
      newErrors.weekStartDate = 'Start date is required';
    }

    if (!formData.weekEndDate) {
      newErrors.weekEndDate = 'End date is required';
    }

    if (formData.weekStartDate && formData.weekEndDate) {
      const startDate = new Date(formData.weekStartDate);
      const endDate = new Date(formData.weekEndDate);
      
      if (startDate >= endDate) {
        newErrors.weekEndDate = 'End date must be after start date';
      }
    }

    // Check if session configuration is available
    if (!sessionConfig || !timeSlots) {
      console.log('Session configuration missing');
      // Create a more user-friendly alert modal
      const alertMessage = 'Session configuration is required to create schedules. Please configure session titles and time slots first.';
      if (window.confirm(alertMessage + '\n\nWould you like to go to Settings to configure sessions now?')) {
        // Navigate to the session configuration page
        window.location.href = '/settings';
      }
      return false;
    }

    // Update assignment dates for all assignments when dates are available
    if (formData.weekStartDate && formData.weekEndDate) {
      const updatedAssignments = assignments.map(assignment => ({
        ...assignment,
        assignmentDate: assignment.assignmentDate || getDateForDay(assignment.dayOfWeek)
      }));
      setAssignments(updatedAssignments);
      
      // After updating assignments, check if any still don't have dates
      const assignmentsWithoutDates = updatedAssignments.filter(assignment => 
        !assignment.assignmentDate && assignment.assignmentType !== 'break'
      );
      if (assignmentsWithoutDates.length > 0) {
        const message = `Unable to calculate dates for ${assignmentsWithoutDates.length} student assignment(s). Please check your date range and try again.`;
        alert(message);
        return false;
      }
    } else {
      // If dates are not set but there are assignments, show error
      if (assignments.length > 0) {
        newErrors.weekStartDate = newErrors.weekStartDate || 'Start date is required when assignments are present';
        newErrors.weekEndDate = newErrors.weekEndDate || 'End date is required when assignments are present';
      }
    }

    // Check for duplicate schedule for same therapist and date range
    if (formData.therapistId && formData.weekStartDate && formData.weekEndDate) {
      // This validation will be handled by the backend, but we can add a warning here
      console.log('Validating for duplicate schedules...');
    }

    console.log('Validation errors:', newErrors);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('Form submission started');
    console.log('Form data:', formData);
    console.log('Assignments:', assignments);
    
    if (!validateForm()) {
      console.log('Form validation failed');
      return;
    }

    setLoading(true);

    try {
      // Helper function to ensure proper time format (HH:MM:SS)
      const formatTime = (time: string): string => {
        if (!time) return time;
        const parts = time.split(':');
        if (parts.length === 2) {
          return `${parts[0]}:${parts[1]}:00`; // Add seconds if missing
        }
        return time; // Already has seconds or invalid format
      };

      let result;

      if (isEditMode && existingSchedule) {
        // For updates, send both schedule fields and assignments
        const updateData = {
          weekStartDate: formData.weekStartDate,
          weekEndDate: formData.weekEndDate,
          workingHoursStart: existingSchedule.workingHoursStart,
          workingHoursEnd: existingSchedule.workingHoursEnd,
          sessionDuration: existingSchedule.sessionDuration,
          assignments: assignments.map(assignment => {
            const participantType = assignment.participantType ||
              (assignment.assignmentType === 'break'
                ? 'break'
                : assignment.guestStudentId || assignment.guestStudent
                  ? 'guest'
                  : 'registered');

            const isGuest = participantType === 'guest';
            const isBreak = participantType === 'break' || assignment.assignmentType === 'break';

            const payload: any = {
              participantType,
              studentId: !isBreak && !isGuest ? assignment.studentId : null,
              guestStudentId: isGuest
                ? assignment.guestStudentId || assignment.guestStudent?.id || null
                : null,
              dayOfWeek: assignment.dayOfWeek,
              startTime: formatTime(assignment.startTime),
              endTime: formatTime(assignment.endTime),
              assignmentType: assignment.assignmentType,
              activityName: !isBreak && assignment.assignmentType !== 'therapy'
                ? assignment.sessionTitle
                : isBreak
                  ? 'Break'
                  : assignment.sessionTitle,
              notes: assignment.notes || null,
              therapistId: assignment.assignmentType === 'therapy' ? existingSchedule.therapistId : null
            };

            if (isGuest && assignment.guestStudent) {
              payload.guestStudent = {
                id: assignment.guestStudent.id,
                fullName: assignment.guestStudent.fullName,
                contactInfo: assignment.guestStudent.contactInfo,
                notes: assignment.guestStudent.notes
              };
            }

            if (isBreak) {
              payload.activityName = 'Break';
            }

            return payload;
          })
        };

        console.log('Schedule update data to send:', updateData);
        console.log('Assignments being sent:', updateData.assignments);
        result = await scheduleApi.update(existingSchedule.id, updateData as Partial<Schedule>);
        console.log('Schedule updated successfully:', result);
        handleSuccess('Schedule updated successfully');
      } else {
        // For creation, include assignments
        const createData = {
          therapistId: parseInt(formData.therapistId),
          weekStartDate: formData.weekStartDate,
          weekEndDate: formData.weekEndDate,
          sessionConfigId: parseInt(formData.sessionConfigId),
          assignments: assignments.map(assignment => {
            const participantType = assignment.participantType ||
              (assignment.assignmentType === 'break'
                ? 'break'
                : assignment.guestStudentId || assignment.guestStudent
                  ? 'guest'
                  : 'registered');

            const isGuest = participantType === 'guest';
            const isBreak = participantType === 'break' || assignment.assignmentType === 'break';

            const payload: any = {
              participantType,
              studentId: !isBreak && !isGuest ? assignment.studentId : null,
              guestStudentId: isGuest
                ? assignment.guestStudentId || assignment.guestStudent?.id || null
                : null,
              guestStudent: isGuest && assignment.guestStudent
                ? {
                    id: assignment.guestStudent.id,
                    fullName: assignment.guestStudent.fullName,
                    contactInfo: assignment.guestStudent.contactInfo,
                    notes: assignment.guestStudent.notes
                  }
                : undefined,
              sessionTitle: assignment.sessionTitle,
              dayOfWeek: assignment.dayOfWeek,
              startTime: formatTime(assignment.startTime),
              endTime: formatTime(assignment.endTime),
              assignmentDate: assignment.assignmentDate,
              notes: assignment.notes || null,
              assignmentType: assignment.assignmentType,
              activityName: isBreak ? 'Break' : assignment.sessionTitle
            };

            if (!isGuest) {
              delete payload.guestStudent;
            }

            return payload;
          })
        };

        console.log('Schedule creation data to send:', createData);
        result = await scheduleApi.create(createData);
        console.log('Schedule created successfully:', result);
        handleSuccess('Schedule created successfully');
      }

      onSubmit(result.data.schedule);
    } catch (error) {
      console.error(`Schedule ${isEditMode ? 'update' : 'creation'} error:`, error);
      handleApiError(error as any, `Failed to ${isEditMode ? 'update' : 'create'} schedule`);
    } finally {
      setLoading(false);
    }
  };

  if (!timeSlots) {
    console.log('Time slots not loaded yet');
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading session configuration...</p>
      </div>
    );
  }

  console.log('Rendering schedule form with time slots:', timeSlots);
  console.log('Session count for Monday:', timeSlots?.Monday?.length);
  console.log('Students loaded:', students);
  console.log('Students count:', students.length);

  // Add debug log for students
  console.log('Students loaded for dropdown:', students);

  return (
    <>
      <div className="modal-overlay" onClick={(e) => {
      // Only close modal if clicking on the overlay itself, not on dropdowns
      if (e.target === e.currentTarget) {
        onCancel();
      }
    }}>
      <div className="modal-content schedule-creation-modal">
        <div className="modal-header">
          <h3>{isEditMode ? 'Edit Schedule' : 'Create New Schedule'}</h3>
          <button className="close-button" onClick={onCancel}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="schedule-creation-form">
          {/* Form Header with Essential Fields */}
          <div className="form-header-section">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="therapistId">Therapist *</label>
                <SimpleDropdown
                  options={therapists.map(therapist => ({
                    value: therapist.id.toString(),
                    label: `${therapist.name} - ${therapist.specialization}`
                  }))}
                  value={formData.therapistId}
                  onChange={(value) => {
                    console.log('Therapist selected:', value);
                    setFormData(prev => ({ ...prev, therapistId: value.toString() }));
                  }}
                  placeholder="Search and select therapist..."
                  className={errors.therapistId ? 'error' : ''}
                />
                {errors.therapistId && <span className="field-error">{errors.therapistId}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="weekStartDate">Week Start Date *</label>
                <input
                  type="date"
                  id="weekStartDate"
                  name="weekStartDate"
                  value={formData.weekStartDate}
                  onChange={handleChange}
                  required
                  className={`form-input ${errors.weekStartDate ? 'error' : ''}`}
                />
                {errors.weekStartDate && <span className="field-error">{errors.weekStartDate}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="weekEndDate">Week End Date *</label>
                <input
                  type="date"
                  id="weekEndDate"
                  name="weekEndDate"
                  value={formData.weekEndDate}
                  onChange={handleChange}
                  required
                  className={`form-input ${errors.weekEndDate ? 'error' : ''}`}
                />
                {errors.weekEndDate && <span className="field-error">{errors.weekEndDate}</span>}
              </div>
            </div>
          </div>

          {/* Schedule Grid */}
          <div className="form-section">
            <h4>Weekly Schedule</h4>
            <div className="schedule-grid">
              <table 
                className="schedule-table"
                style={{
                  '--session-count': timeSlots?.Monday?.length || 15
                } as React.CSSProperties}
              >
                <thead>
                  <tr>
                    <th className="weekday-header">Weekday</th>
                    {timeSlots && timeSlots.Monday && timeSlots.Monday.map((slot, index) => {
                      console.log(`Rendering header for slot ${index}:`, slot);
                      return (
                        <th key={index} className="session-header">
                          <div className="session-title">{slot.session_title}</div>
                          <div className="time-range">{slot.start_time} - {slot.end_time}</div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {days.map(day => (
                    <tr key={day}>
                      <td className="weekday-cell">
                        <strong>{day}</strong>
                      </td>
                      {timeSlots[day as keyof SessionTimeSlotsByDay].map((daySlot, index) => {
                        const assignment: StudentAssignment | undefined = getAssignmentForSlot(day, daySlot.start_time, daySlot.end_time);
                        
                        return (
                          <td key={index} className="assignment-cell">
                            {assignment ? (
                              <div className={`assigned-student ${assignment.assignmentType === 'break' ? 'break-assignment' : ''}`}>
                                <div className="student-name">
                                  {assignment.assignmentType === 'break'
                                    ? 'Break'
                                    : assignment.participantType === 'guest'
                                      ? assignment.guestStudent?.fullName || 'Guest Student'
                                      : (() => {
                                          const student = students.find(s => s.id === assignment.studentId);
                                          return student
                                            ? `${student.first_name} ${student.last_name}`
                                            : 'Unknown Student';
                                        })()}
                                </div>
                                <button
                                  type="button"
                                  className="remove-assignment"
                                  onClick={() => removeStudentAssignment(day, daySlot.start_time, daySlot.end_time)}
                                >
                                  ×
                                </button>
                              </div>
                            ) : (
                              // Show loading/fallback for students
                              students.length === 0 ? (
                                <div style={{fontSize: '0.8rem', color: '#888'}}>Loading...</div>
                              ) : (
                                <SimpleDropdown
                                  options={(() => {
                                    const guestOptions = guestStudents.map(guest => ({
                                      value: `guest:${guest.id}`,
                                      label: `Guest: ${guest.fullName}`
                                    }));

                                    const studentOptions = students.map(student => ({
                                      value: `student:${student.id}`,
                                      label: `${student.first_name} ${student.last_name}`
                                    }));

                                    const options = [
                                      { value: 'guest:new', label: '➕ Add Guest Student' },
                                      ...guestOptions,
                                      ...studentOptions,
                                      { value: 'break', label: 'Break' }
                                    ];
                                    console.log('Student dropdown options:', options);
                                    return options;
                                  })()}
                                  value=""
                                  onChange={(value) => {
                                    console.log('Student dropdown selection:', value);
                                    if (!value || value === '') {
                                      console.log('Empty value selected, ignoring');
                                      return;
                                    }

                                    if (value === 'break') {
                                      const assignmentDate = getDateForDay(day) || '';
                                      const newAssignment: StudentAssignment = {
                                        participantType: 'break',
                                        sessionTitle: daySlot.session_title,
                                        dayOfWeek: day,
                                        startTime: daySlot.start_time,
                                        endTime: daySlot.end_time,
                                        assignmentDate,
                                        notes: '',
                                        assignmentType: 'break'
                                      };
                                      setAssignmentForSlot(newAssignment);
                                      return;
                                    }

                                    if (value === 'guest:new') {
                                      openGuestForm({
                                        day,
                                        sessionTitle: daySlot.session_title,
                                        startTime: daySlot.start_time,
                                        endTime: daySlot.end_time
                                      });
                                      return;
                                    }

                                    if (value.startsWith('guest:')) {
                                      const guestId = Number(value.split(':')[1]);
                                      const guest = guestStudents.find(g => g.id === guestId);
                                      if (guest) {
                                        handleExistingGuestAssignment(
                                          day,
                                          daySlot.session_title,
                                          daySlot.start_time,
                                          daySlot.end_time,
                                          guest
                                        );
                                      }
                                      return;
                                    }

                                    if (value.startsWith('student:')) {
                                      const studentId = value.split(':')[1];
                                      handleRegisteredStudentAssignment(
                                        day,
                                        daySlot.session_title,
                                        daySlot.start_time,
                                        daySlot.end_time,
                                        studentId
                                      );
                                    }
                                  }}
                                  placeholder="Click to assign"
                                  className="student-select"
                                />
                              )
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" onClick={onCancel} className="cancel-button">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="submit-button">
              {loading
                ? (isEditMode ? 'Updating...' : 'Creating...')
                : (isEditMode ? 'Update Schedule' : 'Create Schedule')
              }
            </button>
          </div>
        </form>
      </div>
    </div>

    {guestFormState && (
        <div className="guest-assignment-overlay">
          <div className="guest-assignment-modal">
            <div className="guest-assignment-header">
              <h4>Add Guest Student</h4>
              <button type="button" className="close-button" onClick={closeGuestForm}>×</button>
            </div>
            <p className="guest-assignment-subtitle">
              {guestFormState.sessionTitle} — {guestFormState.day} ({guestFormState.startTime} - {guestFormState.endTime})
            </p>
            <form onSubmit={handleGuestFormSubmit} className="guest-assignment-form">
              <div className="guest-mode-selector">
                <label>
                  <input
                    type="radio"
                    name="guestMode"
                    value="existing"
                    checked={guestFormMode === 'existing'}
                    onChange={() => setGuestFormMode('existing')}
                    disabled={guestStudents.length === 0}
                  />
                  Select Existing Guest
                </label>
                <label>
                  <input
                    type="radio"
                    name="guestMode"
                    value="new"
                    checked={guestFormMode === 'new'}
                    onChange={() => setGuestFormMode('new')}
                  />
                  Create New Guest
                </label>
              </div>

              {guestFormMode === 'existing' ? (
                <div className="form-group">
                  <label htmlFor="existingGuestSelect">Existing Guests</label>
                  <select
                    id="existingGuestSelect"
                    value={selectedExistingGuestId}
                    onChange={(e) => setSelectedExistingGuestId(e.target.value)}
                  >
                    {guestStudents.map(guest => (
                      <option key={guest.id} value={guest.id}>{guest.fullName}</option>
                    ))}
                  </select>
                </div>
              ) : (
                <>
                  <div className="form-group">
                    <label htmlFor="guestFullNameInline">Guest Name *</label>
                    <input
                      id="guestFullNameInline"
                      name="fullName"
                      type="text"
                      value={guestFormData.fullName}
                      onChange={handleGuestFormInputChange}
                      required
                      placeholder="e.g., John Doe"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="guestContactInline">Contact Info</label>
                    <input
                      id="guestContactInline"
                      name="contactInfo"
                      type="text"
                      value={guestFormData.contactInfo}
                      onChange={handleGuestFormInputChange}
                      placeholder="Phone or email"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="guestNotesInline">Notes</label>
                    <textarea
                      id="guestNotesInline"
                      name="notes"
                      value={guestFormData.notes}
                      onChange={handleGuestFormInputChange}
                      placeholder="Additional details (optional)"
                    />
                  </div>
                </>
              )}
              {guestFormError && <div className="field-error">{guestFormError}</div>}
              <div className="guest-assignment-actions">
                <button type="button" className="cancel-button" onClick={closeGuestForm} disabled={guestFormLoading}>
                  Cancel
                </button>
                <button type="submit" className="submit-button" disabled={guestFormLoading}>
                  {guestFormLoading ? 'Saving...' : guestFormMode === 'existing' ? 'Assign Guest' : 'Add Guest'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default ScheduleCreationForm;
